import { type ReactNode, useState } from "react";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import {
  ArrowUpRightIcon,
  CheckIcon,
  CircleIcon,
  ClockIcon,
  LockIcon,
  MinusIcon,
} from "lucide-react";

import { Button, Card } from "@repo/ui";
import { ConfirmSettingModal } from "components";
import { useSnackbar } from "contexts";
import { useCanAccess } from "hooks";
import {
  useApproveArticle,
  useArticleApprovals,
  useArticlePublications,
  usePublishArticle,
} from "hooks/api";
import { useCoverApprovals } from "hooks/api/useArticleCovers";
import type { ArticleTab } from "routes";
import type { WebsiteArticle } from "services/coversApi";

import { formatIsoDay } from "../GtmTradeShowsPage/tradeShowModel";
import {
  approvalKey,
  articleLink,
  articleState,
  currentReviewKey,
  PUBLISH_PERMISSION_TOOLTIP,
  publishConsequence,
  publishTitle,
  reviewSteps,
  type ReviewStep,
  type ReviewStepState,
} from "./articleModel";

/**
 * VRG-733 — the review steps at the top of an article's page (Spencer, 2026-10-29: "I want to see
 * in internal the process I would normally go through to approve the articles and images").
 *
 * Words · Cover · Approve · Narration · Preview · Publish, each with its action ON THE PAGE, so the
 * order is visible and nothing needs the list's ⋮ menu. The states come from `reviewSteps`
 * (articleModel.ts), which also says why Cover comes before Approve (the approval's hash covers
 * the cover). Approve and Publish are the same records and permissions as the list's: approving is
 * platform_admin only (server-enforced), publishing needs `internal_articles_publish`, both behind
 * `ConfirmSettingModal`. Publish here waits for an approval; the list's ⋮ menu can still publish
 * without one, with its own warning, as VRG-685 decided.
 */
export const ArticleReviewSteps = ({
  article,
  onTab,
}: {
  article: WebsiteArticle;
  onTab: (tab: ArticleTab) => void;
}) => {
  const { open: notify } = useSnackbar();
  const { isHasAccess: isPlatformAdmin } = useCanAccess({
    role: "platform_admin",
  });
  const { isHasAccess: canPublish } = useCanAccess({
    action: "internal_articles_publish",
  });
  const { data: approvals } = useArticleApprovals();
  const { data: publications } = useArticlePublications();
  const { data: covers } = useCoverApprovals();
  const approve = useApproveArticle();
  const publish = usePublishArticle();
  const [confirm, setConfirm] = useState<"approve" | "publish" | null>(null);

  const k = approvalKey(article);
  const approval = (approvals ?? []).find((x) => approvalKey(x) === k);
  const publication = (publications ?? []).find((x) => approvalKey(x) === k);
  const state = articleState(article, approval, publication);
  const lockedCover = (covers?.approvals ?? []).find(
    (x) => approvalKey(x) === k,
  );
  const steps = reviewSteps({
    section: article.section,
    state,
    coverLocked: !!lockedCover,
    websiteHasImage: article.cover.type === "image",
  });
  const current = currentReviewKey(steps);
  const link = articleLink({
    path: article.path,
    href: article.href,
    status: state === "published" ? "published" : "staged",
  });

  const action = (s: ReviewStep) => {
    switch (s.key) {
      case "words":
        return (
          <StepLink onClick={() => onTab("overview")}>
            Read on Overview
          </StepLink>
        );
      case "cover":
        return s.state === "skipped" ? null : (
          <StepLink onClick={() => onTab("cover")}>
            {s.state === "done" ? "See the cover" : "Open Cover"}
          </StepLink>
        );
      case "approve":
        if (s.state === "done")
          return approval ? (
            <Typography variant="body4" color="text.secondary">
              {formatIsoDay(approval.approvedAt.slice(0, 10))}
              {approval.approvedBy ? ` by ${approval.approvedBy}` : ""}
            </Typography>
          ) : null;
        return (
          <Gate
            tip={
              !isPlatformAdmin
                ? "Only a Vera admin can approve an article."
                : s.state === "blocked"
                  ? s.note
                  : ""
            }
          >
            <Button
              size="small"
              variant={current === "approve" ? "primary" : "secondary"}
              disableElevation
              disabled={
                !isPlatformAdmin || s.state === "blocked" || approve.isPending
              }
              onClick={() => setConfirm("approve")}
            >
              {state === "changed" ? "Approve this version" : "Approve"}
            </Button>
          </Gate>
        );
      case "preview":
        return (
          <Link
            href={link.href}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <Typography variant="body3">
              {link.label === "Live" ? "Open live page" : "Open staged page"}
            </Typography>
            <ArrowUpRightIcon size={12} strokeWidth={1.5} />
          </Link>
        );
      case "publish":
        if (s.state === "done") return null;
        return (
          <Gate
            tip={
              !canPublish
                ? PUBLISH_PERMISSION_TOOLTIP
                : s.state === "blocked"
                  ? s.note
                  : ""
            }
          >
            <Button
              size="small"
              variant={current === "publish" ? "primary" : "secondary"}
              disableElevation
              disabled={
                !canPublish || s.state === "blocked" || publish.isPending
              }
              onClick={() => setConfirm("publish")}
            >
              Publish
            </Button>
          </Gate>
        );
      default:
        return null;
    }
  };

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
        {steps.map((s, i) => (
          <Box
            key={s.key}
            data-step={s.key}
            data-state={s.state}
            sx={(t) => ({
              flex: "1 1 150px",
              minWidth: 150,
              p: 2.5,
              borderRadius: "8px",
              border: `1px solid ${s.key === current ? t.palette.primary.main : t.palette.primary[100]}`,
              opacity: s.state === "skipped" || s.state === "later" ? 0.6 : 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            })}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <StateIcon state={s.state} />
              <Typography variant="body3" sx={{ fontWeight: 600 }}>
                {i + 1} · {s.label}
              </Typography>
            </Stack>
            <Typography variant="body4" color="text.secondary" sx={{ flex: 1 }}>
              {s.note}
            </Typography>
            <Box>{action(s)}</Box>
          </Box>
        ))}
      </Stack>
      <ConfirmSettingModal
        open={confirm === "approve"}
        onClose={() => setConfirm(null)}
        title="Approve this article?"
        consequence={`This records that "${article.title}" is approved as it stands now: its words and its cover. It does not publish it: the article stays staged until you publish it. If either changes before then, it will show as changed since approval.`}
        confirmLabel="Approve article"
        loading={approve.isPending}
        onConfirm={() =>
          approve.mutate(
            {
              section: article.section,
              slug: article.slug,
              contentHash: article.contentHash,
              title: article.title,
            },
            {
              onSuccess: () => {
                notify({
                  type: "success",
                  text: `Approved “${article.title}”. Awaiting publish.`,
                });
                setConfirm(null);
              },
              onError: (e) =>
                notify({ type: "error", text: (e as Error).message }),
            },
          )
        }
      />
      <ConfirmSettingModal
        open={confirm === "publish"}
        onClose={() => setConfirm(null)}
        title={publishTitle(article.title)}
        consequence={publishConsequence(article.section)}
        confirmLabel="Publish article"
        loading={publish.isPending}
        onConfirm={() =>
          publish.mutate(
            {
              section: article.section,
              slug: article.slug,
              title: article.title,
              contentHash: article.contentHash,
            },
            {
              onSuccess: (p) => {
                notify(
                  p.revalidate.status === "sent"
                    ? {
                        type: "success",
                        text: `Published “${article.title}”. Live on vera.inc within a minute.`,
                      }
                    : {
                        type: "warning",
                        text: `Published “${article.title}”. Website refresh pending: vera.inc picks it up within a minute.`,
                      },
                );
                setConfirm(null);
              },
              onError: (e) =>
                notify({ type: "error", text: (e as Error).message }),
            },
          )
        }
      />
    </Card>
  );
};

const ICONS: Record<ReviewStepState, typeof CheckIcon> = {
  done: CheckIcon,
  todo: CircleIcon,
  blocked: LockIcon,
  skipped: MinusIcon,
  later: ClockIcon,
};

const StateIcon = ({ state }: { state: ReviewStepState }) => {
  const Icon = ICONS[state];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        color: state === "done" ? "success.main" : "text.secondary",
      }}
      aria-label={state}
    >
      <Icon size={14} strokeWidth={2} />
    </Box>
  );
};

const StepLink = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: string;
}) => (
  <Link component="button" type="button" onClick={onClick} underline="hover">
    <Typography variant="body3">{children}</Typography>
  </Link>
);

/** A disabled button still explains itself (§27): the tooltip sits on a wrapper the pointer reaches. */
const Gate = ({ tip, children }: { tip: string; children: ReactNode }) => (
  <Tooltip title={tip}>
    <span style={{ display: "inline-flex" }}>{children}</span>
  </Tooltip>
);
